import { useEffect } from 'react';

import { Alert, Button, Stack, Typography } from '@cast/design-system';

import { raiseReportingMonetizationEvent } from './analytics/reportingMonetizationEvents';
import { isOverPlanCap, isSoftOverage } from './reportingPlans';

type ReportingPlanSoftUpgradeBannerProps = {
  planId: string;
  planName: string;
  planVcpuCap: number;
  avgProvisionedVcpu: number;
  onUpgrade?: () => void;
};

/**
 * Soft nudge when paid usage is over plan cap but under the hard threshold (10%).
 * Access is not blocked.
 */
export const ReportingPlanSoftUpgradeBanner = ({
  planId,
  planName,
  planVcpuCap,
  avgProvisionedVcpu,
  onUpgrade,
}: ReportingPlanSoftUpgradeBannerProps) => {
  const overCap = isOverPlanCap(avgProvisionedVcpu, planVcpuCap);
  const hard = isSoftOverage(avgProvisionedVcpu, planVcpuCap);
  const show = overCap && !hard;

  useEffect(() => {
    if (!show) {
      return;
    }
    raiseReportingMonetizationEvent('reporting_offer_viewed', {
      source: 'soft_upgrade_nudge',
      current_plan: planId,
      selected_bracket: String(planVcpuCap),
    });
  }, [planId, planVcpuCap, show]);

  if (!show) {
    return null;
  }

  return (
    <Alert
      color="warning"
      size="sm"
      testId="reporting-plan-soft-upgrade-banner"
    >
      <Stack gap={8} direction="row" alignItems="center" flexWrap="wrap">
        <Typography variant="P14R" flex={1}>
          Your fleet averages ~{Math.round(avgProvisionedVcpu)} vCPUs, above the{' '}
          {planName} limit ({planVcpuCap}). Upgrade when you can — access is not
          blocked.
        </Typography>
        {onUpgrade ? (
          <Button
            variant="primary"
            size="sm"
            onClick={onUpgrade}
            testId="reporting-plan-soft-upgrade-cta"
          >
            View upgrade options
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
};
